import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { getMe, updateProfile, changePassword } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

export default function AdminProfile() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [profile, setProfile] = useState({
    username: '',
    email: '',
  });
  
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getMe();
        setProfile({
          username: res.data.username || '',
          email: res.data.email || '',
        });
      } catch (err) {
        console.error('Error fetching profile:', err);
        toast.error('Gabim gjatë marrjes së profilit');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSavingProfile(true);

    try {
      const res = await updateProfile(profile);
      // Update localStorage
      const userData = JSON.parse(localStorage.getItem('ad_user') || '{}');
      userData.username = res.data.username;
      localStorage.setItem('ad_user', JSON.stringify(userData));
      
      toast.success('Profili u përditësua me sukses!');
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.response?.data?.detail || 'Gabim gjatë ruajtjes së profilit');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Fjalëkalimet nuk përputhen');
      return;
    }
    
    if (passwords.new_password.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }

    setIsSavingPassword(true);

    try {
      await changePassword({
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      
      toast.success('Fjalëkalimi u ndryshua me sukses!');
      setPasswords({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (err) {
      console.error('Error changing password:', err);
      toast.error(err.response?.data?.detail || 'Gabim gjatë ndryshimit të fjalëkalimit');
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-profile-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Profili Im</h1>
        <p className="text-[#71717A]">Menaxho informacionet e llogarisë tënde</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Profile Info */}
        <div className="bg-white p-8 rounded-sm border border-[#E4E4E7]">
          <h2 className="text-lg font-semibold text-[#0A0A0A] mb-6 flex items-center gap-2">
            <User size={20} className="text-[#D4B982]" />
            Informacionet e Profilit
          </h2>

          <form onSubmit={handleSaveProfile} className="space-y-6">
            <div>
              <Label htmlFor="username" className="flex items-center gap-2 mb-2">
                <User size={16} className="text-[#71717A]" />
                Emri i Përdoruesit
              </Label>
              <Input
                id="username"
                value={profile.username}
                onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                required
                data-testid="profile-username"
              />
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                <Mail size={16} className="text-[#71717A]" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="profile-email"
              />
            </div>

            <Button
              type="submit"
              disabled={isSavingProfile}
              className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] font-semibold"
              data-testid="profile-save"
            >
              {isSavingProfile ? 'Duke ruajtur...' : (
                <>
                  <Save size={18} className="mr-2" />
                  Ruaj Profilin
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white p-8 rounded-sm border border-[#E4E4E7]">
          <h2 className="text-lg font-semibold text-[#0A0A0A] mb-6 flex items-center gap-2">
            <Lock size={20} className="text-[#D4B982]" />
            Ndrysho Fjalëkalimin
          </h2>

          <form onSubmit={handleChangePassword} className="space-y-6">
            <div>
              <Label htmlFor="current_password" className="mb-2 block">
                Fjalëkalimi Aktual
              </Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwords.current_password}
                  onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                  required
                  className="pr-10"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#0A0A0A]"
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="new_password" className="mb-2 block">
                Fjalëkalimi i Ri
              </Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwords.new_password}
                  onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="pr-10"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#0A0A0A]"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-[#71717A] mt-1">Minimum 6 karaktere</p>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="mb-2 block">
                Konfirmo Fjalëkalimin e Ri
              </Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwords.confirm_password}
                onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                required
                data-testid="confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={isSavingPassword}
              className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] font-semibold"
              data-testid="password-save"
            >
              {isSavingPassword ? 'Duke ndryshuar...' : (
                <>
                  <Lock size={18} className="mr-2" />
                  Ndrysho Fjalëkalimin
                </>
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Security Info */}
      <div className="mt-8 p-6 bg-[#F4F4F5] rounded-sm">
        <h3 className="font-semibold text-[#0A0A0A] mb-2">🔒 Siguria</h3>
        <p className="text-sm text-[#71717A]">
          Fjalëkalimi juaj ruhet në databazë me enkriptim <strong>bcrypt</strong> (hash i sigurt). 
          Askush, përfshirë administratorët, nuk mund ta shohë fjalëkalimin tuaj origjinal.
        </p>
      </div>
    </div>
  );
}
